import { CLIENT_SIDE_STORAGE_KEYS } from '../../../constants';
import { LocalStorage } from 'utils';

/**
 * Checks if the app banner should be displayed.
 *
 * This function checks localStorage for a previously saved dismissal state. The state is stored
 * as a string representation of a number ('1' for active, '0' for dismissed).
 *
 * Behavior:
 * - If no value exists in localStorage (first visit): returns `true` (banner should be shown)
 * - If value is '1': returns `true` (banner should be shown)
 * - If value is '0': returns `false` (banner was dismissed)
 *
 * The default behavior of showing the banner on first visit ensures new users see the app promotion,
 * while respecting returning users who have dismissed it.
 *
 * @returns `true` if the banner should be displayed, `false` if it was dismissed
 *
 * @example
 * ```ts
 * // Check if banner should be shown
 * if (shouldShow()) {
 *   setTimeout(() => displayBanner(), 5000);
 * }
 * ```
 *
 * @example
 * ```ts
 * // First time user (no localStorage value)
 * shouldShow(); // returns true
 *
 * // User who dismissed banner
 * dismiss();
 * shouldShow(); // returns false
 *
 * // User who restored banner
 * restore();
 * shouldShow(); // returns true
 * ```
 */
export function shouldShow(): boolean {
  const status = LocalStorage.getItem(CLIENT_SIDE_STORAGE_KEYS.SHOW_APP_BANNER);

  if (status === null) {
    return true; // Default to visible if not set
  }

  return parseInt(status, 10) === 1;
}

/**
 * Permanently dismisses the app banner.
 *
 * This function stores the dismissal state in localStorage, preventing the banner from appearing
 * on future visits. The dismissal persists across browser sessions until the user clears their
 * browser storage or the state is manually restored.
 *
 * Storage format: Stores '0' in localStorage to indicate dismissed state.
 *
 * This function is typically called when the user closes/dismisses the banner.
 *
 * @example
 * ```ts
 * // User dismisses the banner
 * function handleClose() {
 *   dismiss();
 *   hideBanner();
 * }
 * ```
 *
 * @example
 * ```ts
 * // Banner closed, won't show on next visit
 * dismiss();
 * // On page reload:
 * shouldShow(); // returns false
 * ```
 */
export function dismiss(): void {
  LocalStorage.setItem(CLIENT_SIDE_STORAGE_KEYS.SHOW_APP_BANNER, '0');
}
